"use client";

import { useEffect, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { cn } from "@ui/lib";
import { getPlatformLogo } from "@repo/core/client";
import { BentoGrid, BentoTile } from "@marketing/shared/components/bento/Bento";
import { Reveal, useInView } from "@marketing/shared/components/bento/motion";

/**
 * Face-free social proof for the personal AI visibility report page.
 *
 * Most report subjects are individuals nobody would recognize, and we don't
 * want to surface people's faces, so instead of a logo/photo strip this shows
 * an abstract bento: the running report count and the AI engines every report
 * covers.
 *
 * AI-visibility note: the hero number is SERVER-RENDERED as the real total
 * (see AnimatedTotal) — the count-up is a client-only flourish, so crawlers and
 * no-JS clients always read the true figure in the raw HTML. No <noscript>
 * shim needed because the static value is the primary render.
 */

// Marketing display names paired with their canonical platform key, so the
// logo comes from the shared PLATFORM_LOGOS source and stays in sync.
const PLATFORMS: Array<{ key: string; name: string }> = [
	{ key: "openai", name: "ChatGPT" },
	{ key: "claude", name: "Claude" },
	{ key: "gemini", name: "Gemini" },
	{ key: "perplexity", name: "Perplexity" },
	{ key: "google_ai_overview", name: "Google AI Overviews" },
];

// Fixed locale so the server and client format identically (no hydration drift).
const NUMBER_FORMAT = new Intl.NumberFormat("en-US");

function prefersReducedMotion(): boolean {
	return (
		typeof window !== "undefined" &&
		window.matchMedia("(prefers-reduced-motion: reduce)").matches
	);
}

/**
 * Count-up that renders the REAL value on the server (never 0). It only resets
 * to 0 and animates once it scrolls into view on a motion-capable client, so
 * the true total is always present in the server-rendered HTML.
 */
function AnimatedTotal({
	value,
	className,
}: {
	value: number;
	className?: string;
}) {
	const { ref, inView } = useInView<HTMLSpanElement>();
	const [display, setDisplay] = useState(value);
	const started = useRef(false);

	useEffect(() => {
		if (!inView || started.current) return;
		started.current = true;
		if (prefersReducedMotion()) return;

		const durationMs = 1200;
		let raf = 0;
		let start: number | null = null;
		setDisplay(0);
		const tick = (now: number) => {
			if (start === null) start = now;
			const t = Math.min(1, (now - start) / durationMs);
			const eased = 1 - (1 - t) ** 3;
			setDisplay(Math.round(value * eased));
			if (t < 1) {
				raf = requestAnimationFrame(tick);
			} else {
				setDisplay(value);
			}
		};
		raf = requestAnimationFrame(tick);
		return () => cancelAnimationFrame(raf);
	}, [inView, value]);

	return (
		<span ref={ref} className={cn("tabular-nums", className)}>
			{NUMBER_FORMAT.format(display)}
		</span>
	);
}

export function PersonSocialProofBento({ total }: { total: number }) {
	const t = useTranslations();
	const perks = t("marketing.socialProof.personBento.perks")
		.split("·")
		.map((s) => s.trim())
		.filter(Boolean);

	return (
		<Reveal>
			<BentoGrid>
				{/* Hero: running total */}
				<BentoTile
					span="twoThirds"
					tileClassName="relative flex flex-col justify-center gap-4 p-8 sm:p-10"
				>
					<div
						aria-hidden
						className="pointer-events-none absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent"
					/>
					<div className="relative">
						<AnimatedTotal
							value={total}
							className="block text-6xl font-extrabold leading-none tracking-tight text-primary sm:text-7xl"
						/>
						<p className="mt-3 text-lg font-medium text-foreground">
							{t("marketing.socialProof.personBento.reportsLabel")}
						</p>
						{perks.length > 0 ? (
							<div className="mt-5 flex flex-wrap gap-2">
								{perks.map((perk) => (
									<span
										key={perk}
										className="inline-flex items-center rounded-full border bg-background px-3 py-1 text-xs font-semibold text-muted-foreground"
									>
										{perk}
									</span>
								))}
							</div>
						) : null}
					</div>
				</BentoTile>

				{/* Coverage: the AI engines every report checks */}
				<BentoTile
					span="third"
					className="sm:col-span-2"
					tileClassName="flex flex-col justify-center gap-4 p-8"
				>
					<p className="text-sm font-bold uppercase tracking-wide text-primary">
						{t("marketing.socialProof.personBento.platformsHeading")}
					</p>
					<ul className="flex flex-wrap gap-2">
						{PLATFORMS.map(({ key, name }) => {
							const logo = getPlatformLogo(key);
							return (
								<li key={key}>
									<span className="inline-flex items-center gap-2 rounded-lg border bg-muted/50 px-3 py-1.5 text-sm font-semibold text-foreground">
										{logo ? (
											// eslint-disable-next-line @next/next/no-img-element
											<img
												src={logo}
												alt=""
												aria-hidden
												className="size-4 shrink-0"
												loading="lazy"
											/>
										) : null}
										{name}
									</span>
								</li>
							);
						})}
					</ul>
				</BentoTile>
			</BentoGrid>
		</Reveal>
	);
}
